import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import {
  AlertTriangle,
  Check,
  CircleHelp,
  DollarSign,
  FileSignature,
  Info,
  Landmark,
  Lightbulb,
  Loader2,
  PencilLine,
  Phone,
  PhoneOff,
  RotateCcw,
  ShieldCheck,
  X,
} from 'lucide-react';

import { cn } from '../../../lib/utils';

// Confirm service agreement (Nic 2026-10-23): the staff only checklist step.
// When the client signs, the robot calls the VA payment line (3 tries, back
// to back) and the server works out what the payment adds up to on the VA
// pay chart (lib/saCheck).
//
// Less is more (Nic, round 2): nothing said twice, every section obviously
// its own.
//   - The headline: does it match, and (small) when the VA paid and how we
//     checked.
//   - One comparison, no table lines (round 3): quiet labels, what they
//     signed, and what the VA is paying on its own soft panel, so "what they
//     said" and "what the robot found" never blur. A difference: their value
//     bold, the VA's an amber chip; what it's worth is behind a money mark
//     (hover or focus). Monthly pay is the last row.
//   - What did they say? Two plain choices: they confirmed it (Accept, with
//     an optional note right there), or it needs fixing (Correct & resend).
// While the robot calls, it's a live timeline instead.

const money = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signedMoney = (n) => `${n > 0 ? '+' : n < 0 ? '-' : ''}${money(Math.abs(n))}`;
const clock = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
// A plain day ('2026-10-01') read as that day, in any time zone.
const day = (d) => (d ? moment(String(d).slice(0, 10), 'YYYY-MM-DD').format('MMM D') : null);
const RUNNING = ['queued', 'calling'];
const NOTE_MAX = 300;

// ---- While it runs ------------------------------------------------------------

const Elapsed = ({ since }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!since) return null;
  return <span className="tabular-nums">{clock(now - new Date(since).getTime())}</span>;
};

const liveLine = (check, firstName) => {
  const current = check.attempts.find((a) => !a.outcome);
  const n = current?.n || check.attempts.length + 1;
  const of = `Attempt ${n} of ${check.maxAttempts}`;
  switch (check.phase) {
    case 'waiting_line':
      return { text: 'Waiting for a free bot line', since: check.phaseAt };
    case 'lines_busy':
      return { text: 'Every bot line was busy. Trying again shortly.', since: null };
    case 'dialing':
      return { text: `${of}: calling the VA line`, since: current?.startedAt || check.phaseAt };
    case 'on_line':
      return { text: `${of}: on the line with the VA`, since: current?.startedAt || check.phaseAt };
    case 'retrying':
      return { text: 'Trying again now', since: null };
    case 'matching':
      return { text: 'Matching the payment against the VA pay chart', since: null };
    default:
      return { text: `Getting ready to call the VA about ${firstName || 'them'}`, since: null };
  }
};

// Every call, one line each: what happened, how long it took.
const Timeline = ({ check, live, firstName }) => {
  const done = check.attempts.filter((a) => a.outcome);
  const line = live ? liveLine(check, firstName) : null;
  return (
    <ol className="mb-0 list-none space-y-[9px] pl-0">
      <li className="flex items-center gap-[10px] text-[12.5px] text-slate-500 dark:text-slate-400">
        <span className="flex h-[16px] w-[16px] shrink-0 items-center justify-center">
          <span className="h-[6px] w-[6px] rounded-full bg-slate-300 dark:bg-slate-600" />
        </span>
        Started when {firstName || 'they'} signed, {moment(check.startedAt).format('MMM D, h:mm A')}
      </li>
      {done.map((a) => {
        const ok = a.outcome === 'paid' || a.outcome === 'no_payment';
        return (
          <li key={a.n} className="flex items-center gap-[10px] text-[12.5px]">
            <span
              className={cn(
                'flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full',
                ok
                  ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300'
                  : 'bg-slate-200/80 text-slate-500 dark:!bg-white/10 dark:text-slate-400',
              )}
            >
              {ok ? <Check size={10} strokeWidth={3} /> : <X size={10} strokeWidth={3} />}
            </span>
            <span className="min-w-0 text-slate-700 dark:text-slate-200">
              <span className="font-semibold">Attempt {a.n}:</span> {a.reason}
            </span>
            {a.startedAt && a.endedAt ? (
              <span className="ml-auto shrink-0 tabular-nums text-[11.5px] text-slate-400">
                {clock(new Date(a.endedAt) - new Date(a.startedAt))}
              </span>
            ) : null}
          </li>
        );
      })}
      {line ? (
        <li className="flex items-center gap-[10px] text-[12.5px] font-semibold text-sky-800 dark:text-sky-200">
          <span className="relative flex h-[16px] w-[16px] shrink-0 items-center justify-center">
            <span className="absolute inline-flex h-[10px] w-[10px] animate-ping rounded-full bg-sky-400 opacity-60 motion-reduce:animate-none" />
            <span className="relative inline-flex h-[8px] w-[8px] rounded-full bg-sky-500" />
          </span>
          <span className="min-w-0">{line.text}</span>
          {line.since ? (
            <span className="ml-auto shrink-0 text-[11.5px] font-medium text-sky-600/70 dark:text-sky-300/70">
              <Elapsed since={line.since} />
            </span>
          ) : null}
        </li>
      ) : null}
    </ol>
  );
};


// ---- The headline -------------------------------------------------------------

const TONE = {
  good: { chip: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300', Icon: ShieldCheck },
  off: { chip: 'bg-amber-500/15 text-amber-600 dark:text-amber-300', Icon: AlertTriangle },
  unsure: { chip: 'bg-slate-500/10 text-slate-500 dark:text-slate-300', Icon: CircleHelp },
  none: { chip: 'bg-slate-500/10 text-slate-500 dark:text-slate-300', Icon: PhoneOff },
};

// At 10% and 20% the VA pays the same with or without a spouse or kids.
const familyHidden = (check) => {
  const fit = check.best || null;
  return fit && fit.familyKnown === false ? fit.rating : null;
};
const familyNote = (check) => {
  const r = familyHidden(check);
  return r ? `Spouse and kids can't be checked at ${r}%: the VA pays the same either way.` : null;
};

const callsSummary = (check) => {
  // A reason that already ends in a period never gets a second one.
  const reasons = [...new Set(check.attempts.filter((a) => a.outcome).map((a) => String(a.reason || '').replace(/\.$/, '')))];
  if (!reasons.length) return null;
  return reasons.length === 1 ? `Every call: ${reasons[0]}.` : `Calls: ${reasons.join('; ')}.`;
};

// Three tiers (Nic 2026-10-23: make it OBVIOUS when the veteran needs
// asking). Green: looks good, Accept. Amber: ask the veteran, and the headline
// names exactly what (only rating, spouse and kids drive the agreement).
// Grey: the bot couldn't check, confirm what they signed. SMC-K never asks:
// it only squares the math, so a different SMC-K is a quiet line under a
// green "Matches", and Accept goes with the VA's.
const ASK_WORDS = { rating: 'rating', married: 'spouse', under18: 'kids', inSchool: 'kids' };
// 'their rating', 'their spouse and kids', 'their rating, spouse and kids'.
const askAbout = (check) => {
  const words = [...new Set(check.diffs.map((d) => ASK_WORDS[d.field]).filter(Boolean))];
  if (!words.length) return 'what they signed';
  const list = words.length === 1 ? words[0] : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
  return `their ${list}`;
};
const smcKLine = (check) => {
  if (!check.best || !check.diffs.some((d) => d.field === 'smcK')) return null;
  const k = (n) => (n ? String(n) : 'none');
  return `SMC-K: the VA pays ${k(check.best.smcK)}, the agreement says ${k(check.signed.smcK)}. It doesn't affect the agreement, so Accept goes with the VA's.`;
};

const headOf = (check, verdictKey, who = 'them') => {
  const join = (...bits) => bits.filter(Boolean).join(' ') || null;
  switch (verdictKey) {
    case 'match':
      return { tone: 'good', title: 'Matches the VA', note: smcKLine(check) };
    case 'rating_only_match':
      return { tone: 'good', title: 'The rating matches the VA', note: join(check.note, smcKLine(check)) };
    case 'mismatch':
      return {
        tone: 'off',
        title: `Ask ${who} about ${askAbout(check)}`,
        note: join(familyNote(check), check.diffs.some((d) => d.field === 'smcK') ? "SMC-K differs too, which doesn't affect the agreement." : null),
      };
    case 'unclear':
      // The money can't confirm the rating, so their word decides (Nic
      // 2026-10-23: a 30-second step, not an investigation). The clues below
      // are a bonus.
      return { tone: 'off', title: "We can't confirm their rating from the VA", note: `Simply reconfirm with ${who}.` };
    case 'no_answer':
      // One line for why; every call is behind the calls link (Nic 2026-10-23).
      // With a try left (Nic 2026-10-25) just the why: the line under it says
      // what to do. The VA not recognizing them reads as one plain sentence.
      if (check.retry?.available) {
        return {
          tone: 'none',
          title: "The bot couldn't get an answer",
          note: check.retry.reason === 'identity' ? "The VA line didn't recognize this SSN and date of birth." : callsSummary(check),
        };
      }
      return { tone: 'none', title: "The bot couldn't get an answer", note: join(callsSummary(check), `Simply reconfirm with ${who}.`) };
    case 'cannot_call':
      if (check.retry?.available) return { tone: 'none', title: "The bot couldn't call", note: check.reason };
      return { tone: 'none', title: "The bot couldn't call", note: join(check.reason, `Simply reconfirm with ${who}.`) };
    default:
      return { tone: 'unsure', title: '', note: null };
  }
};

// ---- The comparison -----------------------------------------------------------

export const SMCK_TIP =
  "SMC-K: extra VA pay (about $140 a month each) for losing the use of a hand, foot, eye, or a reproductive organ (often ED). It's only here so the math matches the VA's payment: it doesn't change their agreement's fee. Not sure? Use what the VA pays.";

// A small (i) with a short explanation on hover or focus (Nic 2026-10-23:
// SMC-K must never be a tripping point).
const InfoTip = ({ text, label }) => (
  <span className="group relative inline-flex shrink-0">
    <button
      type="button"
      aria-label={label || 'What is this?'}
      className="flex h-[16px] w-[16px] items-center justify-center rounded-full text-slate-400 outline-none hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:text-slate-500 dark:hover:text-slate-300"
    >
      <Info size={12} />
    </button>
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-full left-0 z-20 mb-[6px] hidden w-[250px] whitespace-normal rounded-lg bg-slate-900 px-[10px] py-[8px] text-[11.5px] font-normal leading-snug text-white shadow-lg group-focus-within:block group-hover:block dark:!bg-slate-700"
    >
      {text}
    </span>
  </span>
);

const FACTS = [
  { key: 'rating', label: 'Rating', show: (v) => (v == null ? '' : `${v}%`) },
  { key: 'married', label: 'Spouse', show: (v) => (v == null ? null : v ? 'Married' : 'Single') },
  { key: 'under18', label: 'Kids under 18', show: (v) => (v == null ? null : String(v)) },
  { key: 'inSchool', label: 'Kids 18 to 24 in school', show: (v) => (v == null ? null : String(v)) },
  { key: 'smcK', label: 'SMC-K', show: (v) => (v ? String(v) : 'None') },
];

// A small money mark that shows what a difference is worth on hover (or
// keyboard focus), so the amounts never crowd the comparison.
const MoneyTip = ({ amount, label }) => (
  <span className="group relative inline-flex">
    <button
      type="button"
      aria-label={`${signedMoney(amount)} a month${label ? ` ${label}` : ''}`}
      className="flex h-[18px] w-[18px] items-center justify-center rounded-full text-amber-600 outline-none hover:bg-amber-500/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 dark:text-amber-300"
    >
      <DollarSign size={13} strokeWidth={2.5} />
    </button>
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-full right-0 z-20 mb-[6px] hidden whitespace-nowrap rounded-lg bg-slate-900 px-[10px] py-[6px] text-[11.5px] font-semibold text-white shadow-lg group-focus-within:block group-hover:block dark:!bg-slate-700"
    >
      {signedMoney(amount)} a month{label ? <span className="font-normal text-slate-300"> {label}</span> : null}
    </span>
  </span>
);

// What they signed beside what the VA is paying. Three columns, three
// roles: quiet labels, their values, and the robot's findings on one soft
// rounded panel. No lines; rows held by spacing. A difference: their value
// bold, the VA's an amber chip with its worth on hover.
const ROW = 'flex h-[34px] items-center';
// "Couldn't pin it down" (Nic 2026-10-23): the VA column claims nothing it
// doesn't know. Every fact reads "Not clear"; the real monthly pay stays.
const Compare = ({ check }) => {
  const fit = check.best || null;
  const unclear = check.verdict === 'unclear';
  // No answer, or no call at all (Nic 2026-10-23): the specialist still needs
  // what they signed in front of them to confirm it on the phone.
  const noVa = check.status === 'no_answer' || check.status === 'cannot_call';
  const noVaText = check.status === 'cannot_call' ? 'Not called' : 'No answer';
  const noPayment = !fit && check.va == null && check.attempts.some((a) => a.outcome === 'no_payment');
  const off = new Map(check.diffs.map((d) => [d.field, d]));
  const hiddenAt = familyHidden(check);
  // A family fact the amount can't show (10% / 20%).
  const notShown = (f) => noVa || (unclear && !fit) || (!!fit && fit[f.key] == null && hiddenAt != null);
  const vaValue = (f) => {
    if (noVa) return noVaText;
    if (unclear && !fit) return f.key === 'rating' ? "Can't determine" : 'Not clear';
    if (notShown(f)) return `Not shown at ${hiddenAt}%`;
    if (fit) return f.show(fit[f.key]);
    if (noPayment) return f.key === 'rating' ? 'No payment' : '';
    return '';
  };
  const signedPay = check.signed.pay?.total ?? null;
  const vaPay = check.va ? Number(check.va.amount) : noPayment ? 0 : null;
  const gap = signedPay != null && vaPay != null ? Math.round((vaPay - signedPay) * 100) / 100 : 0;
  const vaHead = 'VA is paying';
  const showPay = signedPay != null && (vaPay != null || noVa);

  const head = 'flex h-[30px] items-center gap-[6px] text-[11.5px] font-semibold';
  return (
    <div className="flex text-[13px]">
      {/* Labels */}
      <div className="min-w-0 flex-[1.15] pr-[8px]">
        <div className={head} />
        {FACTS.map((f) => (
          <div key={f.key} className={cn(ROW, 'gap-[4px] text-[12px] font-medium text-slate-400 dark:text-slate-500')}>
            <span className="truncate">{f.label}</span>
            {f.key === 'smcK' ? <InfoTip text={SMCK_TIP} label="What is SMC-K?" /> : null}
          </div>
        ))}
        {showPay ? (
          <div className={cn(ROW, 'mt-[6px] text-[12px] font-semibold text-slate-500 dark:text-slate-400')}>Monthly pay</div>
        ) : null}
      </div>

      {/* They signed */}
      <div className="mr-[8px] min-w-0 flex-1 rounded-xl bg-sky-50/80 px-[12px] pb-[4px] dark:!bg-sky-400/[0.07]">
        <div className={cn(head, 'text-sky-700 dark:text-sky-300')}>
          <FileSignature size={13} className="shrink-0" />
          They signed
        </div>
        {FACTS.map((f) => (
          <div
            key={f.key}
            className={cn(
              ROW,
              off.has(f.key) && f.key !== 'smcK' ? 'font-semibold text-slate-900 dark:text-slate-50' : 'text-slate-600 dark:text-slate-300',
            )}
          >
            <span className="truncate">{f.show(check.signed[f.key])}</span>
          </div>
        ))}
        {showPay ? (
          <div className={cn(ROW, 'mt-[6px] font-semibold tabular-nums text-slate-900 dark:text-slate-50')}>{money(signedPay)}</div>
        ) : null}
      </div>

      {/* VA is paying: one soft panel */}
      <div className="min-w-0 flex-[1.2] rounded-xl bg-slate-100/70 px-[12px] pb-[4px] dark:!bg-white/[0.05]">
        <div className={cn(head, 'text-slate-600 dark:text-slate-300')}>
          <Landmark size={13} className="shrink-0" />
          {vaHead}
        </div>
        {FACTS.map((f) => {
          const d = off.get(f.key);
          return (
            <div key={f.key} className={cn(ROW, 'justify-between gap-[6px]')}>
              {d && f.key === 'smcK' ? (
                <span className="min-w-0 truncate rounded-md bg-slate-200/70 px-[7px] py-[2px] font-semibold text-slate-700 dark:bg-white/10 dark:text-slate-200">
                  {vaValue(f)}
                </span>
              ) : d ? (
                <span className="min-w-0 truncate rounded-md bg-amber-400/20 px-[7px] py-[2px] font-semibold text-amber-800 dark:bg-amber-400/15 dark:text-amber-200">
                  {vaValue(f)}
                </span>
              ) : (
                <span
                  className={cn(
                    'min-w-0 truncate',
                    unclear && !fit && f.key === 'rating'
                      ? 'text-[12px] font-semibold text-amber-700 dark:text-amber-300'
                      : notShown(f)
                        ? 'text-[12px] text-slate-400 dark:text-slate-500'
                        : 'text-slate-600 dark:text-slate-300',
                  )}
                >
                  {vaValue(f)}
                </span>
              )}
              {d?.amount ? <MoneyTip amount={d.amount} /> : null}
            </div>
          );
        })}
        {showPay && noVa ? (
          <div className={cn(ROW, 'mt-[6px] text-[12px] text-slate-400 dark:text-slate-500')}>{noVaText}</div>
        ) : showPay ? (
          <div className={cn(ROW, 'mt-[6px] justify-between gap-[6px] font-semibold tabular-nums text-slate-900 dark:text-slate-50')}>
            <span>{money(vaPay)}</span>
            {gap ? (
              <MoneyTip amount={gap} label="from what they signed for" />
            ) : (
              <Check size={14} strokeWidth={2.5} className="text-emerald-500" aria-label="Same as they signed for" />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const fitWords = (f) => {
  const bits = [`${f.rating}%`];
  if (f.familyKnown) {
    bits.push(f.married ? 'married' : 'single');
    const kids = (f.under18 || 0) + (f.inSchool || 0);
    if (kids) bits.push(`${kids} ${kids === 1 ? 'kid' : 'kids'}`);
  }
  if (f.smcK) bits.push(`${f.smcK} SMC-K`);
  if (f.smcS) bits.push('SMC-S');
  return bits.join(', ');
};

// ---- What did they say? ---------------------------------------------------------

// Depth, not outlines (Nic, round 4): cards lift off the page on a soft
// shadow with a hairline edge. (An arbitrary shadow: Bootstrap's shadow-*
// utilities are !important and fight Tailwind's.)
const LIFT =
  'bg-white shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_14px_-4px_rgba(15,23,42,0.08)] ring-1 ring-slate-900/[0.05] dark:!bg-slate-800/60 dark:shadow-none dark:ring-white/10';

// An icon in a soft round chip, the same shape across the panel.
const Chip = ({ icon: Icon, tone, size = 30 }) => (
  <span className={cn('flex shrink-0 items-center justify-center rounded-full', tone)} style={{ width: size, height: size }}>
    <Icon size={Math.round(size / 2) + 1} />
  </span>
);

// One of the two choices: an icon, a plain question, what it means, its button.
const Choice = ({ icon, tone, title, body, children }) => (
  <div className="flex min-w-0 flex-col py-[14px] first:pt-0 last:pb-0 sm:px-[18px] sm:py-0 sm:first:pl-0 sm:last:pr-0">
    <div className="flex items-start gap-[10px]">
      {React.createElement(icon, { size: 16, strokeWidth: 2.25, className: cn('mt-[1px] shrink-0', tone), 'aria-hidden': true })}
      <div className="min-w-0">
        <div className="text-[13.5px] font-semibold leading-tight text-slate-900 dark:text-slate-50">{title}</div>
        <div className="mt-[3px] text-[12px] leading-relaxed text-slate-500 dark:text-slate-400">{body}</div>
      </div>
    </div>
    <div className="mt-[14px] flex flex-1 flex-col justify-end gap-[8px]">{children}</div>
  </div>
);
// A tile's header band (Nic, round 7: "more titley"): a softly tinted strip
// across the top with a hairline under it. Rounded itself (not clipped by
// the tile) so a repaint can never square its corners.
const BAND =
  '-mx-[18px] -mt-[18px] rounded-t-2xl border-0 border-b border-t-[3px] border-solid border-b-slate-100 px-[18px] pb-[14px] pt-[13px] dark:border-b-white/10';
const BAND_TONE = {
  good: 'border-t-emerald-400 dark:border-t-emerald-400/70',
  off: 'border-t-amber-400 dark:border-t-amber-400/70',
  neutral: 'border-t-slate-300 dark:border-t-slate-500',
  ask: 'border-t-sky-400 dark:border-t-sky-400/70',
};
const TITLE = 'text-[16px] font-semibold leading-tight text-slate-900 dark:text-slate-50';
const ASK_CHIP = 'bg-sky-500/15 text-sky-600 dark:text-sky-300';
// The choices under the main tile use bare colored icons (Nic 2026-10-23: a
// circle on every icon was trying too hard).
const GOOD_ICON = 'text-emerald-500 dark:text-emerald-400';
const FIX_ICON = 'text-amber-500 dark:text-amber-400';
const ASK_ICON = 'text-sky-500 dark:text-sky-400';
// The finding's small print: a phone with the attempts, a dollar with the payment day.
const META_PILL =
  'inline-flex items-center gap-[4px] whitespace-nowrap rounded-full bg-slate-100 px-[8px] py-[2px] text-[11.5px] font-medium tabular-nums text-slate-500 outline-none hover:bg-slate-200/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:!bg-white/[0.06] dark:text-slate-400 dark:hover:!bg-white/10';
// The same short hover as the money marks, above the pill. Narrow, the pills
// sit under the title on the left, so it opens rightward; wider, they sit at
// the card's right edge, so it opens leftward. Either way it stays in the card.
const MetaPill = ({ icon, label, sub, children }) => (
  <span className="group relative inline-flex">
    <button type="button" aria-label={`${label} ${sub}`} className={META_PILL}>
      {icon}
      {children}
    </button>
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-full left-0 sm:left-auto sm:right-0 z-20 mb-[6px] hidden whitespace-nowrap rounded-lg bg-slate-900 px-[10px] py-[6px] text-[11.5px] font-semibold text-white shadow-lg group-focus-within:block group-hover:block dark:!bg-slate-700"
    >
      {label}
      <span className="font-normal text-slate-300"> {sub}</span>
    </span>
  </span>
);

// text-[#fff], not text-white: Bootstrap's .text-white is !important and
// would beat dark:text-slate-900, leaving white text on the white dark-mode
// button (Nic 2026-10-23).
const BTN_DARK =
  'inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-3.5 py-2 text-[13px] font-semibold text-[#fff] outline-none hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 disabled:cursor-not-allowed disabled:opacity-50 dark:!bg-white dark:text-slate-900 dark:hover:!bg-slate-200';
const BTN_GREEN =
  'inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-[13px] font-semibold text-white outline-none hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60 disabled:cursor-not-allowed disabled:opacity-50';
const BTN_LIGHT =
  'inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-800 outline-none ring-1 ring-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 disabled:cursor-not-allowed disabled:opacity-50 dark:!bg-white/5 dark:text-slate-100 dark:ring-white/15 dark:hover:!bg-white/10';

// onCorrect (Nic 2026-10-23): Correct & resend goes to the Manage tab's
// Service agreement card (ClientWorkspace), never a second agreement here.
// The one more try, and what to do first.
// About them in its own teal, so the eye finds the card on the left.
const AboutThem = () => <span className="font-semibold text-teal-700 dark:text-teal-300">About them</span>;
export const RETRY_HINT = {
  identity: (who, status) =>
    status === 'cannot_call' ? (
      <>
        Get with {who} and add it in <AboutThem />.
      </>
    ) : (
      <>
        Get with {who} and verify the SSN and date of birth in <AboutThem />.
      </>
    ),
  va_line: () => 'You can try once more later.',
};
const RetryBlock = ({ retry, status, who, canAct, starting, onStart }) =>
  retry.available ? (
    <div className="mt-[8px]">
      <div className="text-[12.5px] text-slate-600 dark:text-slate-300">{(RETRY_HINT[retry.reason] || RETRY_HINT.va_line)(who, status)}</div>
      <button
        type="button"
        disabled={!canAct || starting}
        onClick={onStart}
        className="mt-[6px] inline-flex items-center gap-[6px] rounded-md border-0 bg-transparent p-0 text-[12.5px] font-medium text-sky-700 hover:underline disabled:opacity-50 dark:text-sky-300"
      >
        Use your 1 retry
        {/* A phone after the words (Nic 2026-10-25): it places the calls. */}
        {starting ? <Loader2 size={13} className="animate-spin" /> : <Phone size={13} />}
      </button>
    </div>
  ) : (
    <div className="mt-[8px] text-[12.5px] text-slate-500 dark:text-slate-400">
      Already tried once more.
    </div>
  );

const SaCheckPanel = ({ check, firstName, need, canAct, accepting, starting, onAccept, onStart, onCorrect }) => {
  const [note, setNote] = useState('');
  const who = firstName || 'them';

  const live = !!check && RUNNING.includes(check.status);
  const verdictKey = !check
    ? null
    : check.status === 'done'
      ? check.verdict
      : ['no_answer', 'cannot_call'].includes(check.status)
        ? check.status
        : null;
  const good = verdictKey === 'match' || verdictKey === 'rating_only_match';
  const accepted = check?.accepted || null;
  const head = check && verdictKey ? headOf(check, verdictKey, who) : null;
  const tone = head ? TONE[head.tone] : null;
  const keyOf = (f) => [f.rating, f.married, f.under18, f.inSchool, f.smcK, f.smcS].join('|');
  const otherFits = check?.best ? check.fits.filter((f) => keyOf(f) !== keyOf(check.best)) : [];
  const calls = check ? check.attempts.filter((a) => a.outcome).length : 0;

  const noteField = (
    <label className="block">
      <span className="sr-only">Note</span>
      <textarea
        value={note}
        maxLength={NOTE_MAX}
        disabled={!canAct}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="Note (optional): what they told you"
        className="w-full resize-none rounded-lg border border-solid border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] text-slate-800 outline-none placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-400/25 disabled:opacity-60 dark:border-white/10 dark:!bg-white/5 dark:text-slate-100"
      />
    </label>
  );
  const accept = (label) => (
    <button type="button" disabled={!canAct || accepting} onClick={() => onAccept(note.trim())} className={BTN_DARK}>
      {accepting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} strokeWidth={2.5} />}
      {label}
    </button>
  );
  const correct = (
    <button type="button" disabled={!canAct} onClick={onCorrect} className={BTN_LIGHT}>
      <PencilLine size={14} />
      Correct &amp; resend
    </button>
  );

  // What did they say? (or, once accepted, who confirmed it)
  const decide = accepted ? (
    <div className="flex items-start gap-[10px] rounded-xl bg-emerald-500/[0.07] px-[14px] py-[12px] text-[13px] text-slate-700 dark:text-slate-200">
      <span className="mt-[1px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300">
        <Check size={11} strokeWidth={3} />
      </span>
      <div className="min-w-0">
        <div className="font-semibold">
          Confirmed by {accepted.byName || 'their specialist'}, {moment(accepted.at).format('MMM D, h:mm A')}
        </div>
        {accepted.note ? <div className="mt-[2px] text-[12.5px] text-slate-500 dark:text-slate-400">&ldquo;{accepted.note}&rdquo;</div> : null}
      </div>
    </div>
  ) : good ? (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <span className="text-[12.5px] text-slate-500 dark:text-slate-400">Nothing to fix. Accept it to finish this step.</span>
      <button type="button" disabled={!canAct || accepting} onClick={() => onAccept('')} className={BTN_GREEN}>
        {accepting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} strokeWidth={2.5} />}
        Accept
      </button>
    </div>
  ) : (
    <div className={cn('rounded-2xl p-[18px]', LIFT)}>
      <div className={cn(BAND, BAND_TONE.ask, 'mb-[18px] flex items-center gap-[12px]')}>
        <Chip icon={Phone} tone={ASK_CHIP} size={32} />
        <div className="min-w-0">
          <div className={TITLE}>
            {check ? `Confirm it with ${who} on the phone` : `Check it with ${who}`}
          </div>
          <div className="mt-[2px] text-[12px] text-slate-500 dark:text-slate-400">Then pick what happens next.</div>
        </div>
      </div>
      <div className="grid divide-y divide-slate-200 sm:grid-cols-2 sm:divide-x sm:divide-y-0 dark:divide-white/10">
        {check ? (
          <Choice
            icon={Check}
            tone={GOOD_ICON}
            title={verdictKey === 'mismatch' || verdictKey === 'unclear' ? 'They confirmed what they signed' : 'They confirmed it on the phone'}
            body={
              verdictKey === 'mismatch'
                ? 'The agreement stays as is. Your note goes in History.'
                : 'The agreement stays as is, on their word.'
            }
          >
            {noteField}
            {accept(verdictKey === 'mismatch' ? 'Accept as signed' : 'Accept after a phone check')}
          </Choice>
        ) : (
          <Choice icon={RotateCcw} tone={ASK_ICON} title="Run the VA check" body="3 calls, a few minutes. The result shows here.">
            <button type="button" disabled={!canAct || starting} onClick={onStart} className={BTN_DARK}>
              {starting ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              Run the VA check
            </button>
          </Choice>
        )}
        {check && onCorrect ? (
          <Choice icon={PencilLine} tone={FIX_ICON} title="Something needs fixing" body="Correct the facts and send them a new agreement to sign.">
            {correct}
          </Choice>
        ) : (
          <Choice icon={Check} tone={GOOD_ICON} title="They confirmed it on the phone" body="Accept it without a VA check, on their word.">
            {noteField}
            {accept('Accept after a phone check')}
          </Choice>
        )}
      </div>
    </div>
  );

  return (
    <div className="mt-1.5">
      {need ? <p className="mb-0 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">{need}</p> : null}

      {live ? (
        <div className={cn('mt-[16px] min-h-[170px] rounded-2xl px-[18px] py-[16px]', LIFT)}>
          <div className="mb-[14px] flex items-center gap-[10px]">
            <span className="flex h-[32px] w-[32px] items-center justify-center rounded-full bg-sky-500/10 text-sky-600 dark:text-sky-300">
              <Loader2 size={16} className="animate-spin motion-reduce:animate-none" />
            </span>
            <div>
              <div className="text-[15px] font-semibold text-slate-900 dark:text-slate-50">Checking with the VA</div>
              <div className="text-[12px] text-slate-500 dark:text-slate-400">
                Up to {check.maxAttempts} calls, back to back. This updates on its own.
              </div>
            </div>
          </div>
          <Timeline check={check} live firstName={firstName} />
        </div>
      ) : (
        <>
          {/* The finding */}
          <div className={cn('mt-[16px] rounded-2xl p-[18px]', LIFT)}>
            {check ? (
              <div className={cn(BAND, BAND_TONE[head.tone === 'good' || head.tone === 'off' ? head.tone : 'neutral'], 'flex items-start gap-[12px]')}>
                <span className={cn('flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-full', tone.chip)}>
                  <tone.Icon size={16} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-x-[12px] gap-y-[6px]">
                    <div className={TITLE}>{head.title}</div>
                    {check.va?.paymentDate || calls ? (
                      // Two quiet pills (Nic 2026-10-23): a phone with the attempts, a
                      // dollar with the payment day, each with a short hover like the
                      // money marks. They drop under the title when the card is narrow.
                      <div className="flex shrink-0 items-center gap-[6px]">
                        {calls ? (
                          <MetaPill
                            icon={<Phone size={11} aria-hidden />}
                            label={`${calls} ${calls === 1 ? 'call' : 'calls'}`}
                            sub="to the VA line"
                          >
                            {calls}
                          </MetaPill>
                        ) : null}
                        {check.va?.paymentDate ? (
                          <MetaPill
                            icon={<DollarSign size={11} strokeWidth={2.5} aria-hidden />}
                            label={day(check.va.paymentDate)}
                            sub="the VA's last payment"
                          >
                            {day(check.va.paymentDate)}
                          </MetaPill>
                        ) : check.attempts.some((a) => a.outcome === 'no_payment') ? (
                          <MetaPill
                            icon={<DollarSign size={11} strokeWidth={2.5} aria-hidden />}
                            label="No payment"
                            sub="on file with the VA"
                          >
                            None
                          </MetaPill>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  {head.note ? (
                    <div className="mt-[4px] text-[13px] text-slate-600 dark:text-slate-300">{head.note}</div>
                  ) : null}
                  {check.retry && onStart && !accepted ? (
                    // ONE more try, only for a reason someone can act on (Nic
                    // 2026-10-25): their SSN or birthday (fix them in About
                    // them first), or the VA line not answering. Never for
                    // the bot's own problem, never twice.
                    <RetryBlock retry={check.retry} status={check.status} who={who} canAct={canAct} starting={starting} onStart={onStart} />
                  ) : null}
                </div>
              </div>
            ) : (
              <div className={cn(BAND, BAND_TONE.neutral, 'flex items-start gap-[12px]')}>
                <span className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-full bg-slate-500/10 text-slate-500 dark:text-slate-300">
                  <CircleHelp size={16} />
                </span>
                <div className="min-w-0">
                  <div className={TITLE}>No VA check for {who} yet</div>
                  <div className="mt-[4px] text-[12.5px] text-slate-500 dark:text-slate-400">They signed before the check started.</div>
                </div>
              </div>
            )}

            {check && ['done', 'no_answer', 'cannot_call'].includes(check.status) ? (
              <div className="mt-[18px]">
                <Compare check={check} />
                {check.hints?.length ? (
                  // Clues, not homework: what might explain the dollar gap.
                  <ul className="mb-0 mt-[10px] list-none space-y-[3px] pl-0">
                    {check.hints.map((h) => (
                      <li key={h} className="flex gap-[7px] text-[11.5px] leading-relaxed text-slate-500 dark:text-slate-400">
                        <Lightbulb size={13} className="mt-[2px] shrink-0 text-amber-500/80" />
                        {h}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {otherFits.length ? (
                  <div className="mt-[8px] text-[11.5px] text-slate-400">
                    The amount also fits {otherFits.slice(0, 3).map(fitWords).join(' · ')}
                    {otherFits.length > 3 ? ` and ${otherFits.length - 3} more` : ''}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          {/* What did they say? */}
          <div className="mt-[14px]">{decide}</div>
        </>
      )}

    </div>
  );
};

export default SaCheckPanel;
